// The one header every artifact pane wears (#11548 r51/r60, #12481, #13261):
// the artifact's type tile, its title and a caret, which together are the
// one button that opens the title's menu (the title stays selectable
// text, and a drag that selects it opens nothing, #14012), which lists
// every action but Chat (Hunter: "all of our options should be in the name
// dropdown (and the important options replicated as buttons up top)");
// then whatever the
// pane's state adds beside it, then the reading aids, then the cluster, in
// one order: a draft's Save, Chat, Share, Export or Download, Comments, the
// sources count, a hairline, the mode toggle, Close last. No version numbers
// in the row (Gerry: "the v1 v2 stuff is bad"); while an earlier version is
// on screen the row says so, with the way back. It folds as the pane narrows
// (#12091, Gerry): the reading aids stand down first, then the buttons fold
// into the title's menu while Chat shrinks to its icon, then the sources
// count stands down. At phone it is always folded. Every pane shares this
// one header so they cannot drift apart.
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { artifactUpdatedLabel } from "./ArtifactSources";
import {
  ChevronDown,
  ChevronLeft,
  History,
  MessageSquare,
  Undo2,
  X,
} from "lucide-react";
import { Tooltip } from "@mui/material";
import { Menu } from "@/components/FpPages/RMS/design";
import { useIsPhoneViewport } from "@/hooks/useViewportTier";
import { TypeTile } from "../FpPages/Artifacts/artifactBadge";
import {
  artifactToolbarButtonClass,
  paneToolbarDisabledClass,
  paneToolbarToggleClass,
} from "./paneToolbar";
import type { ArtifactVersion } from "../FpPages/Artifacts/useArtifacts";

/** The artifact's version history, for an artifact with a registry row:
 *  the history column's toggle (in the title's menu) and, while an earlier
 *  version is on screen, the way back to the live content. */
export interface ArtifactVersionControl {
  /** The earlier version on screen, or null for the live content. */
  viewing: number | null;
  /** Show this earlier version's snapshot, or the live content for null —
   *  the same callback the history column gets. */
  onView: (version: ArtifactVersion | null) => void;
  /** Whether the history column is open. */
  historyOpen: boolean;
  onToggleHistory: () => void;
}

/** A document's comments (#13194): the column's toggle, an icon button
 *  with a dot while any thread is open, and "Comments (N)" in the title's
 *  menu once the cluster folds. */
export interface ArtifactCommentsControl {
  openCount: number;
  /** Whether the comments column is open: the button is pressed. */
  open: boolean;
  onToggle: () => void;
}

const commentsName = (openCount: number): string =>
  openCount > 0 ? `Comments, ${openCount} open` : "Comments";

/** The comments toggle: the comment glyph as an icon button like the
 *  other header icons, pressed while the column is open, with a small dot
 *  at its top-right while the document has an open thread. The count is in
 *  its name and in the column's head, not on the icon. */
const CommentsButton: React.FC<{ control: ArtifactCommentsControl }> = ({
  control,
}) => {
  const hasOpen = control.openCount > 0;
  return (
    <Tooltip
      title={control.open ? "Close comments" : "Comments"}
      arrow
      placement="bottom"
      describeChild
    >
      <button
        type="button"
        aria-label={commentsName(control.openCount)}
        aria-pressed={control.open}
        data-has-open={hasOpen ? "true" : "false"}
        onClick={control.onToggle}
        className={`relative ${paneToolbarToggleClass(control.open)}`}
      >
        <MessageSquare className="h-4 w-4" aria-hidden="true" />
        {hasOpen && (
          <span
            aria-hidden="true"
            className="absolute right-[5px] top-[5px] h-1.5 w-1.5 rounded-full bg-[#2A3DCB] ring-2 ring-[var(--fp-surface)]"
          />
        )}
      </button>
    </Tooltip>
  );
};

/** An action's glyph: lucide's, or phosphor's where the app's vocabulary
 *  draws the verb in phosphor (Export, rules/frontend.md). */
export type PaneGlyph = React.ComponentType<{
  className?: string;
  size?: number | string;
  "aria-hidden"?: boolean;
}>;

/** One header action: a button with a tooltip while the cluster is unfolded
 *  (a link, for an action with an `href`), a labeled item in the title's
 *  menu once it folds, unless it is `buttonOnly`. */
export interface PaneAction {
  key: string;
  label: string;
  icon: PaneGlyph;
  /** Runs the action. `anchor` is the control it was started from: the
   *  row's button, or, from the title's menu, that button when it is in the
   *  row and the title otherwise, so an action that opens a menu of its own
   *  (Export) opens it in one place. */
  onSelect: (anchor: HTMLElement) => void;
  /** Where the action goes, for an action that opens a page (Open
   *  interaction, #14869): its control is a link to this address, so a
   *  modifier or middle click opens it in a new tab as any link does, and a
   *  plain click runs `onSelect` (the router's navigate) instead of loading
   *  the page afresh. A link action is never `disabled`: the link has no
   *  disabled state. */
  href?: string;
  disabled?: boolean;
  /** Pressed state (version history open). */
  active?: boolean;
  /** A labeled button while unfolded (a draft's Save). */
  emphasis?: boolean;
  /** The labeled button's fill: ink for the one likely action, the surface
   *  for the rest. Ink by default. */
  tone?: "ink" | "surface";
  /** A quiet word button while unfolded (Edit): hairline, ink text, named
   *  by the word it shows; the tooltip and the menu item keep the label. */
  word?: boolean;
  /** The word the emphasised or word button shows, when shorter than the
   *  label (the emphasised button's accessible name keeps the full label). */
  buttonLabel?: string;
  /** The title menu's wording, when it says more than the button's name
   *  ("Export…", which opens a further menu). */
  menuLabel?: string;
  /** The tooltip, when it says more than the label ("Copied!"). */
  hint?: string;
  /** Lives in the title's menu at every width (Copy link, a hand edit),
   *  never as a button of its own. */
  secondary?: boolean;
  /** A button while the row has room AND an item in the title's menu at
   *  every width: the important actions repeated up top (Share, Export,
   *  Download; #13261). */
  pinned?: boolean;
  /** Never in the title's menu: a word button while the row has room, its
   *  icon alone once the row folds and at phone (Chat; #13261, Hunter: "i
   *  think we could exclude Chat from the title menu"; Open interaction,
   *  #14869). */
  buttonOnly?: boolean;
  /** Rendered after the sources count, beside Close: the layout toggle,
   *  which is about the pane and not the artifact. */
  trailing?: boolean;
  /** Starts a new section in the menu (a change of object). */
  dividerBefore?: boolean;
}

/** What the header has stood down, in order: nothing, the reading aids,
 *  then the primary action buttons too, then the sources chip too (a pane
 *  too tight even for the folded cluster, as at phone). */
export type HeaderFold = "none" | "aids" | "actions" | "compact";

const FOLD_RANK: Record<HeaderFold, number> = {
  none: 0,
  aids: 1,
  actions: 2,
  compact: 3,
};

// The least the title keeps: a few characters. Under this the header is all
// chrome and no subject, so the cluster folds instead.
const MIN_TITLE_PX = 56;
const GAP_PX = 8;
// The cluster's own gap (gap-1), and an icon button's width (h-8 w-8): what
// a folded button gives back, and what Chat keeps when it shrinks.
const CLUSTER_GAP_PX = 4;
const ICON_BUTTON_PX = 32;

/** A plain left click: no key held that asks the browser for a new tab or
 *  window. (A middle click never reaches `onClick`: Chrome sends it as
 *  `auxclick`, and the link opens a new tab on its own.) */
const isPlainClick = (event: React.MouseEvent): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/** An action's control: a button, or for an action with an `href` a link
 *  to it, so the browser's own new-tab gestures work on it. */
const ActionControl: React.FC<{
  action: PaneAction;
  className: string;
  /** The control's accessible name, when its visible content is not. */
  label?: string;
  pressed?: boolean;
  children: React.ReactNode;
}> = ({ action, className, label, pressed, children }) => {
  if (action.href === undefined) {
    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={pressed}
        data-pane-action={action.key}
        onClick={(event) => action.onSelect(event.currentTarget)}
        disabled={action.disabled}
        className={className}
      >
        {children}
      </button>
    );
  }
  return (
    <a
      href={action.href}
      aria-label={label}
      data-pane-action={action.key}
      onClick={(event) => {
        // A click with a modifier key is the browser's: a new tab or window.
        if (!isPlainClick(event)) return;
        event.preventDefault();
        action.onSelect(event.currentTarget);
      }}
      className={className}
    >
      {children}
    </a>
  );
};

/** One action as its unfolded button. Shared with the document viewer's
 *  artifact cluster so the two headers keep one button style. */
export const PaneActionButton: React.FC<{
  action: PaneAction;
  /** A word button drawn as its icon alone: the row has folded (Chat). */
  iconOnly?: boolean;
}> = ({ action, iconOnly = false }) => {
  const Icon = action.icon;
  if (action.word && !iconOnly) {
    const word = action.buttonLabel ?? action.label;
    const button = (
      <ActionControl
        action={action}
        className="mr-1 flex h-7 flex-none cursor-pointer items-center gap-1.5 rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] pl-2 pr-2.5 text-[13px] font-medium text-[var(--fp-ink)] hover:border-[var(--fp-ink)] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[var(--fp-border-strong)]"
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {word}
      </ActionControl>
    );
    // The word is the button's name; a tooltip only when it says more.
    const hint = action.hint ?? action.label;
    return hint === word ? (
      button
    ) : (
      <Tooltip title={hint} arrow placement="bottom" describeChild>
        <span>{button}</span>
      </Tooltip>
    );
  }
  if (action.emphasis) {
    return (
      <Tooltip
        title={action.hint ?? action.label}
        arrow
        placement="bottom"
        // The title describes the button; it must not become an aria-label
        // on the wrapping span, a second element with the button's name.
        describeChild
      >
        <span>
          <ActionControl
            action={action}
            label={action.label}
            className={`mx-1 flex h-8 items-center gap-1.5 rounded-[7px] px-3 text-[12.5px] font-medium disabled:opacity-60 ${
              action.tone === "surface"
                ? "border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] text-[var(--fp-ink)] [box-shadow:var(--fp-lift)] hover:border-[var(--fp-ink)]"
                : "bg-[var(--fp-ink)] text-[var(--fp-surface)] hover:opacity-90"
            }`}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {action.buttonLabel ?? action.label}
          </ActionControl>
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={action.hint ?? action.label}
      arrow
      placement="bottom"
      // The title describes the button; it must not become an aria-label
      // on the wrapping span, a second element with the button's name.
      describeChild
    >
      <span>
        <ActionControl
          action={action}
          label={action.label}
          pressed={action.active}
          // The toggle class, not the resting class plus an overlay: two
          // `text-…` utilities on one element let the stylesheet's order
          // pick, and the resting ink won (#11852).
          className={`${
            action.active === undefined
              ? artifactToolbarButtonClass
              : paneToolbarToggleClass(action.active)
          } ${paneToolbarDisabledClass}`}
        >
          <Icon className="h-4 w-4" />
        </ActionControl>
      </span>
    </Tooltip>
  );
};

interface ArtifactPaneHeaderProps {
  /** The artifact's name: the title's text and its menu button's name. */
  title: string;
  /** The type tile before the title: the extension or short name, and the
   *  kind word it stands for. */
  badge?: { ext: string; kind: string };
  /** Where the artifact came from and when it last changed: the first
   *  lines of the title's menu, as Claude's panel states them (#12481,
   *  Gerry: no "· Chat" after the title). */
  origin?: string | null;
  /** ISO instant; the "Updated N ago" line is worded when the menu renders,
   *  so it never goes stale in a long-open pane. */
  updatedAt?: string | null;
  /** Version history, for an artifact with a registry row. */
  version?: ArtifactVersionControl;
  /** Comments, for a document artifact (#13194). */
  comments?: ArtifactCommentsControl;
  /** A source view (a document opened from an artifact's citation): the
   *  way back to that artifact leads the row, before the document's own
   *  tile and title. */
  returnTo?: {
    title: string;
    badge: { ext: string; kind: string };
    onReturn: () => void;
  };
  /** A small tag after the title saying what the document is here
   *  ("Source · p. 4"). */
  tag?: string;
  /** The sources count: a quiet button in the cluster, the last thing to
   *  stand down as the pane narrows. */
  sources?: React.ReactNode;
  /** Beside the subject and never folded: an edit's Save and Cancel, the
   *  editor's own controls. */
  leading?: React.ReactNode;
  /** The reading aids (zoom): the first to stand down. */
  aids?: React.ReactNode;
  actions: PaneAction[];
  onClose: () => void;
  /** Dialogs the actions open, rendered outside the header row. */
  children?: React.ReactNode;
}

const ArtifactPaneHeader: React.FC<ArtifactPaneHeaderProps> = ({
  title,
  badge,
  origin,
  updatedAt,
  version,
  comments,
  returnTo,
  tag,
  sources,
  leading,
  aids,
  actions,
  onClose,
  children,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLDivElement>(null);
  // The title's box and its text: the difference is the title's fixed
  // chrome (the tile, the caret, the padding), which never gives way.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleTextRef = useRef<HTMLSpanElement>(null);
  const leadingRef = useRef<HTMLDivElement>(null);
  const aidsRef = useRef<HTMLDivElement>(null);
  const clusterRef = useRef<HTMLDivElement>(null);
  // Remembered while rendered, so the folded→unfolded direction has widths
  // to add back: a hidden group cannot be measured. Deciding from remembered
  // full widths rather than the rendered ones is what keeps the fold from
  // flip-flopping — hiding a group removes the overflow that hid it.
  const aidsWidthRef = useRef<number | null>(null);
  const clusterWidthRef = useRef<number | null>(null);
  // The cluster as it measures once the primary buttons have folded: what
  // decides whether the sources count must stand down too.
  const foldedClusterWidthRef = useRef<number | null>(null);
  // What folding gives back, remembered while the row is unfolded, so the
  // folded cluster can be estimated in the same pass that folds it rather
  // than measured a render later.
  const primaryWidthRef = useRef<number | null>(null);
  const [fold, setFold] = useState<HeaderFold>("none");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // The name's selected text, or "" when no selection sits in the name.
  const selectedTitleText = useCallback((): string => {
    const selection = window.getSelection();
    const titleText = titleTextRef.current;
    return selection &&
      !selection.isCollapsed &&
      titleText &&
      titleText.contains(selection.anchorNode)
      ? selection.toString()
      : "";
  }, []);
  // What the name had selected when the press on the title began.
  const pressSelectionRef = useRef("");
  const notePressSelection = useCallback((): void => {
    pressSelectionRef.current = selectedTitleText();
  }, [selectedTitleText]);
  // The menu stays shut only for the click that ends a press which made or
  // changed a selection in the name: that press was a drag selecting the
  // name for copying, and the menu must not open over it (#14012). Every
  // other click opens it: a keyboard Enter or Space (detail 0), a click with
  // no selection, a selection elsewhere on the page, and a selection left
  // over from before, which Chrome keeps through a press on the tile or the
  // caret (they cannot be selected) and through a press inside the
  // selection itself (it collapses only after the click).
  const openTitleMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      const before = pressSelectionRef.current;
      pressSelectionRef.current = "";
      if (event.detail !== 0) {
        const after = selectedTitleText();
        if (after !== "" && after !== before) return;
      }
      setMenuAnchor(event.currentTarget);
    },
    [selectedTitleText]
  );
  // At phone the header fits close (rules/frontend.md, #13261): the tile,
  // the title, Chat as its icon, Comments and Close; Share and Export or
  // Download are in the title's menu, and the sources count stands down.
  const phone = useIsPhoneViewport();
  const hasAids = Boolean(aids);
  const folded = useCallback(
    (level: HeaderFold): boolean => FOLD_RANK[fold] >= FOLD_RANK[level],
    [fold]
  );
  // The row, in the list's order: a draft's Save, Chat, Share, Export or
  // Download. Everything but Chat folds into the title's menu.
  const row = actions.filter((a) => !a.secondary && !a.trailing);
  // What joins the menu's head once the row folds: the buttons the menu
  // does not already list (a draft's Save).
  const primary = row.filter((a) => !a.pinned && !a.buttonOnly);
  const compact = FOLD_RANK[fold] >= FOLD_RANK.compact;
  // The menu lists every action but Chat, in the list's order (#13261 R4):
  // Share, Export or Download, Copy link, Link to record, a hand edit, Copy;
  // then Version history and Comments in a section of their own, at every
  // width: the row carries no version numbers (Gerry).
  const secondary: PaneAction[] = [
    ...actions.filter((a) => a.secondary || a.pinned),
    ...(version
      ? [
          {
            key: "versions",
            label: "Version history",
            icon: History,
            active: version.historyOpen,
            onSelect: version.onToggleHistory,
            secondary: true,
            dividerBefore: true,
          } satisfies PaneAction,
        ]
      : []),
    // Comments is listed after Version history at every width (#13261 R4);
    // its toggle is in the row too until the row folds, and at phone.
    ...(comments
      ? [
          {
            key: "comments",
            label:
              comments.openCount > 0
                ? `Comments (${comments.openCount})`
                : "Comments",
            icon: MessageSquare,
            active: comments.open,
            onSelect: comments.onToggle,
            secondary: true,
            dividerBefore: !version,
          } satisfies PaneAction,
        ]
      : []),
  ];
  const trailing = actions.filter((a) => a.trailing);
  // The menu is there whenever it has something to hold: the secondary
  // actions at every width, the primary ones too once folded.
  const menuActions = folded("actions")
    ? [...primary, ...secondary]
    : secondary;
  // The cluster's membership: a change (an edit starting, a draft saved)
  // makes the remembered widths stale, so both are measured afresh from an
  // unfolded render — in a layout effect, so nothing paints in between.
  const viewingEarlier = version !== undefined && version.viewing !== null;
  const membership = `${actions.map((a) => a.key).join("|")}#${hasAids}#${viewingEarlier}#${Boolean(sources)}#${Boolean(comments)}#${phone}`;
  const membershipRef = useRef(membership);

  useLayoutEffect(() => {
    if (membershipRef.current !== membership) {
      membershipRef.current = membership;
      aidsWidthRef.current = null;
      clusterWidthRef.current = null;
      foldedClusterWidthRef.current = null;
      primaryWidthRef.current = null;
    }
    // A folded row with no widths remembered cannot decide whether it fits,
    // and folded it cannot measure them: it unfolds to measure afresh. A
    // change of membership leaves it so, and so does a fold decided before
    // that change and applied after it, which otherwise stays folded at any
    // width (CI run 40723366517).
    if (fold !== "none" && clusterWidthRef.current === null) {
      setFold("none");
      return;
    }
    const header = headerRef.current;
    if (!header) return;
    // The header's content box: what its children divide up.
    const contentWidthOf = (el: HTMLElement): number => {
      const style = getComputedStyle(el);
      return (
        el.clientWidth -
        parseFloat(style.paddingLeft) -
        parseFloat(style.paddingRight)
      );
    };
    const measure = (contentWidth: number): void => {
      if (!folded("aids") && aidsRef.current) {
        aidsWidthRef.current = aidsRef.current.offsetWidth;
      }
      if (!folded("actions") && clusterRef.current) {
        clusterWidthRef.current = clusterRef.current.offsetWidth;
      }
      if (!folded("actions") && clusterRef.current) {
        // What folding gives back: every button that leaves the row, and
        // Chat's word beside its icon.
        primaryWidthRef.current = Array.from(
          clusterRef.current.querySelectorAll<HTMLElement>("[data-folds]")
        ).reduce(
          (sum, el) =>
            sum +
            (el.dataset.folds === "shrinks"
              ? Math.max(0, el.offsetWidth - ICON_BUTTON_PX)
              : el.offsetWidth + CLUSTER_GAP_PX),
          0
        );
      }
      if (fold === "actions" && clusterRef.current) {
        foldedClusterWidthRef.current = clusterRef.current.offsetWidth;
      }
      const cluster = clusterWidthRef.current;
      // Measured once the buttons have folded; until then, estimated from
      // the full cluster less the buttons, so a pane dragged narrow settles
      // on its level in one pass.
      const foldedCluster =
        foldedClusterWidthRef.current ??
        (cluster === null ? null : cluster - (primaryWidthRef.current ?? 0));
      // Unmeasurable until the cluster has rendered once; leave the
      // decision alone rather than guess.
      if (cluster === null) return;
      // The subject's fixed parts (the tile, the caret, the earlier-version
      // note) travel with the title; only the title's text gives way.
      // Summed part by part: the subject box is the flexible one and
      // reports its slack as width.
      const titleChrome =
        titleRef.current && titleTextRef.current
          ? titleRef.current.offsetWidth - titleTextRef.current.offsetWidth
          : 0;
      const subjectFixed =
        titleChrome +
        Array.from(subjectRef.current?.children ?? [])
          .filter(
            (child): child is HTMLElement =>
              child instanceof HTMLElement && child !== titleRef.current
          )
          .reduce((sum, child) => sum + child.offsetWidth + GAP_PX, 0);
      const leadingWidth = leadingRef.current?.offsetWidth ?? 0;
      const aidsWidth = hasAids ? (aidsWidthRef.current ?? 0) : 0;
      const gaps =
        GAP_PX * (1 + (leadingWidth > 0 ? 1 : 0) + (hasAids ? 1 : 0));
      const room =
        contentWidth - MIN_TITLE_PX - subjectFixed - leadingWidth - gaps;
      setFold(
        aidsWidth + cluster <= room
          ? "none"
          : cluster <= room
            ? "aids"
            : foldedCluster === null || foldedCluster <= room
              ? "actions"
              : "compact"
      );
    };
    // Observed: the header, and each group inside it. A group can change
    // width without the header moving — the zoom controls arrive once the
    // text has loaded, "Copied!" replaces a tooltip, the sources count lands
    // — and the remembered widths must follow, or a group that grew
    // after the last decision overflows the header (caught on CI, where
    // the text loads after the first measure).
    const observer = new ResizeObserver(() => measure(contentWidthOf(header)));
    observer.observe(header);
    // The subject's fixed box, not the subject: the subject is flex-1 and
    // sized by the header, so it never resizes when something inside it
    // arrives.
    for (const group of [leadingRef, aidsRef, clusterRef]) {
      if (group.current) observer.observe(group.current);
    }
    return () => observer.disconnect();
  }, [fold, folded, hasAids, membership]);

  return (
    <>
      <div
        ref={headerRef}
        // The card's first row, with a hairline under it (Gerry, PR #12706:
        // "add a separation between the top of the artifact panel and the
        // nav toolbars"): the pane's chrome and the artifact's own toolbar
        // (a PDF's page nav, a workbook's sheet strip, a table's bar) read
        // as two rows on every artifact type.
        className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-[var(--fp-border)] px-4"
      >
        <div
          ref={subjectRef}
          className="flex min-w-0 flex-1 items-center gap-2"
        >
          {/* A source view's way back (Gerry, 2026-10-06): the artifact
              the document was cited from, quiet, ahead of the document's
              own tile and title. */}
          {returnTo && (
            <button
              type="button"
              aria-label={`Back to ${returnTo.title}`}
              onClick={returnTo.onReturn}
              className="-ml-1.5 flex h-8 min-w-0 max-w-[40%] flex-none cursor-pointer items-center gap-1.5 rounded-[7px] px-1.5 text-[13px] font-medium text-[var(--fp-ink-2)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)]"
            >
              <ChevronLeft
                aria-hidden="true"
                className="h-4 w-4 flex-none text-[var(--fp-ink-3)]"
              />
              <TypeTile
                ext={returnTo.badge.ext}
                kind={returnTo.badge.kind}
                size="sm"
              />
              <span className="min-w-0 truncate">{returnTo.title}</span>
            </button>
          )}
          {/* The title is the pane's menu (#12481; Gerry's "quiet
              cluster", 2026-10-06): the tile, the name and a caret are one
              button with one hover wash behind all three. It holds the
              secondary actions, and the primary ones too once the pane is
              too tight for their buttons. The name inside it is still
              ordinary selectable text: a drag highlights it for copying,
              and a drag that selected it opens nothing (#14012). Plain text
              when there is no menu to open. */}
          <h2
            ref={titleRef}
            // Named by the title alone, not the tile's kind word with it.
            aria-label={title}
            className="flex min-w-0 text-[14px] font-semibold tracking-[-0.011em] text-[var(--fp-ink)]"
          >
            {menuActions.length > 0 ? (
              <button
                type="button"
                aria-label={title}
                aria-haspopup="menu"
                aria-expanded={menuAnchor !== null}
                onMouseDown={notePressSelection}
                onClick={openTitleMenu}
                className={`${returnTo ? "" : "-ml-1.5"} flex h-8 min-w-0 cursor-pointer select-none items-center gap-2 rounded-[7px] px-1.5 hover:bg-[var(--fp-hover)] ${
                  menuAnchor !== null ? "bg-[var(--fp-hover)]" : ""
                }`}
              >
                {badge && (
                  <TypeTile ext={badge.ext} kind={badge.kind} size="sm" />
                )}
                <span
                  ref={titleTextRef}
                  className="min-w-0 cursor-text select-text truncate"
                >
                  {title}
                </span>
                <ChevronDown
                  aria-hidden="true"
                  className="h-3.5 w-3.5 flex-none text-[var(--fp-ink-3)]"
                />
              </button>
            ) : (
              <span className="flex min-w-0 items-center gap-2">
                {badge && (
                  <TypeTile ext={badge.ext} kind={badge.kind} size="sm" />
                )}
                <span ref={titleTextRef} className="min-w-0 truncate">
                  {title}
                </span>
              </span>
            )}
          </h2>
          {/* What the document is here, in the Task tag's grammar
              (RunWorkflowRail's task pane). */}
          {tag && (
            <span className="inline-flex flex-none items-center whitespace-nowrap rounded-[5px] bg-[var(--fp-accent-soft)] px-1.5 py-[2px] text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--fp-accent)]">
              {tag}
            </span>
          )}
          {/* An earlier version on screen: said in words, beside the title
              it belongs to, with the way back. */}
          {viewingEarlier && version && (
            <span className="flex flex-none items-center gap-1 text-[12.5px] text-[var(--fp-ink-3)]">
              <span className={phone ? "sr-only" : undefined}>
                · Earlier version
              </span>
              <button
                type="button"
                onClick={() => version.onView(null)}
                className="flex h-7 cursor-pointer items-center gap-1 rounded-[6px] px-2 font-medium text-[var(--fp-ink)] hover:bg-[var(--fp-hover)]"
              >
                <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                Back to latest
              </button>
            </span>
          )}
        </div>
        {leading && (
          <div ref={leadingRef} className="flex flex-none items-center gap-3">
            {leading}
          </div>
        )}
        {hasAids && !folded("aids") && (
          <div ref={aidsRef} className="flex flex-none items-center gap-2">
            {aids}
          </div>
        )}
        <div ref={clusterRef} className="flex flex-none items-center gap-1">
          {row.map((action) =>
            action.buttonOnly ? (
              <span
                key={action.key}
                className="inline-flex"
                data-folds={folded("actions") || phone ? undefined : "shrinks"}
              >
                <PaneActionButton
                  action={action}
                  iconOnly={folded("actions") || phone}
                />
              </span>
            ) : folded("actions") || (phone && action.pinned) ? null : (
              <span key={action.key} className="inline-flex" data-folds="">
                <PaneActionButton action={action} />
              </span>
            )
          )}
          {/* The comments toggle follows Export (#13261 R1); at phone it
              never folds: the icon is the way into the sheet (#13194). */}
          {comments && (phone || !folded("actions")) && (
            <span className="inline-flex" data-folds={phone ? undefined : ""}>
              <CommentsButton control={comments} />
            </span>
          )}
          {/* The sources count follows the actions; it stands down at
              phone and in a pane too tight for it, and takes no room when
              nothing is cited. */}
          {!phone && !compact && sources && (
            <span className="inline-flex flex-none items-center empty:hidden">
              {sources}
            </span>
          )}
          {/* A hairline between the artifact's actions and the pane's own
              controls (the canvas's divider). */}
          {trailing.length > 0 && (
            <span
              aria-hidden="true"
              className="mx-1 h-5 w-px flex-none bg-[var(--fp-border)]"
            />
          )}
          {trailing.map((action) => (
            <PaneActionButton key={action.key} action={action} />
          ))}
          <Tooltip title="Close" arrow placement="bottom">
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className={artifactToolbarButtonClass}
            >
              <X className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>
      </div>
      <Menu
        anchorEl={menuAnchor}
        open={menuAnchor !== null}
        onClose={() => setMenuAnchor(null)}
        label="Artifact actions"
        head={
          origin || updatedAt ? (
            <>
              {origin && <div>{origin}</div>}
              {updatedAt && <div>{artifactUpdatedLabel({ updatedAt })}</div>}
            </>
          ) : undefined
        }
        items={menuActions.map((action) => ({
          label: action.menuLabel ?? action.label,
          // The same icon as the button it stands in for (Gerry).
          icon: action.icon,
          disabled: action.disabled,
          dividerBefore: action.dividerBefore,
          onSelect: () => {
            // The row's own button when it is there, else the title the
            // menu is open on.
            const inRow =
              clusterRef.current?.querySelector<HTMLElement>(
                `[data-pane-action="${action.key}"]`
              ) ?? null;
            const anchor = inRow ?? menuAnchor;
            setMenuAnchor(null);
            // Only narrows the type: an item can be picked only while the
            // menu is open, on its anchor.
            if (anchor !== null) action.onSelect(anchor);
          },
        }))}
      />
      {children}
    </>
  );
};

export default ArtifactPaneHeader;
